import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Copy, Check, Trash2, Search, Users, Eye, Settings, Crown, Package, Rocket, X, UserPlus, KeyRound, Unlock, Lock } from 'lucide-react';
import AddClientModal, { GenerateLoginModal } from './AddClientModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [userOverview, setUserOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [changingPlan, setChangingPlan] = useState(false);
  const [agents, setAgents] = useState([]);
  const [assignAgentOpen, setAssignAgentOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignmentMode, setAssignmentMode] = useState('manual');
  const [addOpen, setAddOpen] = useState(false);
  const [loginUser, setLoginUser] = useState(null);

  const handleToggleActivation = async (user) => {
    const next = !user.isActivated;
    try {
      await axios.put(`${API}/admin/users/${user.principalId}/activation`, { isActivated: next });
      toast({ title: '✅ Success', description: next ? 'Dashboard unlocked for client' : 'Dashboard locked (₹9 activation required)' });
      fetchUsers();
    } catch (error) {
      toast({ title: '❌ Error', description: 'Failed to update activation', variant: 'destructive' });
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchAgents();
    fetchAssignmentMode();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/admin/sales-agents`);
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchAssignmentMode = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/assignment-mode`);
      setAssignmentMode(response.data.mode);
    } catch (error) {
      console.error('Error fetching assignment mode:', error);
    }
  };

  const handleToggleAssignmentMode = async () => {
    const newMode = assignmentMode === 'manual' ? 'auto' : 'manual';
    try {
      await axios.put(`${API}/admin/settings/assignment-mode`, { mode: newMode });
      setAssignmentMode(newMode);
      toast({
        title: '✅ Success',
        description: `Assignment mode set to ${newMode}`,
      });
    } catch (error) {
      toast({
        title: '❌ Error',
        description: 'Failed to update assignment mode',
        variant: 'destructive',
      });
    }
  };

  const handleOpenAssignAgent = (user) => {
    setSelectedUser(user);
    setSelectedAgent(user.assignedAgent || '');
    setAssignAgentOpen(true);
  };

  const handleAssignAgent = async () => {
    if (!selectedUser) return;
    setAssigning(true);
    try {
      if (selectedAgent === 'auto') {
        // Auto-assign using round-robin
        await axios.post(`${API}/admin/auto-assign-client/${selectedUser.principalId}`);
      } else {
        // Manual assignment
        await axios.put(`${API}/admin/assign-client/${selectedUser.principalId}`, {
          agentId: selectedAgent || null,
          assignedBy: 'admin',
          assignmentType: 'manual'
        });
      }
      toast({
        title: '✅ Success',
        description: 'Agent assigned successfully',
      });
      setAssignAgentOpen(false);
      fetchUsers();
    } catch (error) {
      toast({
        title: '❌ Error',
        description: error.response?.data?.detail || 'Failed to assign agent',
        variant: 'destructive',
      });
    } finally {
      setAssigning(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This will remove all associated data.')) {
      return;
    }
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast({
        title: '✅ Success',
        description: 'User deleted successfully',
      });
      fetchUsers();
    } catch (error) {
      toast({
        title: '❌ Error',
        description: 'Failed to delete user',
        variant: 'destructive',
      });
    }
  };

  const handleViewOverview = async (user) => {
    setSelectedUser(user);
    setOverviewOpen(true);
    setLoadingOverview(true);
    try {
      const response = await axios.get(`${API}/admin/user-overview/${user.principalId}`);
      setUserOverview(response.data);
    } catch (error) {
      console.error('Error fetching user overview:', error);
      toast({
        title: '❌ Error',
        description: 'Failed to load user overview',
        variant: 'destructive',
      });
    } finally {
      setLoadingOverview(false);
    }
  };

  const handleOpenChangePlan = (user) => {
    setSelectedUser(user);
    setSelectedPlan(user.activePlan || '');
    setChangePlanOpen(true);
  };

  const handleChangePlan = async () => {
    if (!selectedUser) return;
    setChangingPlan(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.principalId}/change-plan`, {
        planName: selectedPlan === 'none' ? null : (selectedPlan || null)
      });
      toast({
        title: '✅ Success',
        description: `Plan changed to ${selectedPlan === 'none' ? 'None' : (selectedPlan || 'None')} successfully`,
      });
      setChangePlanOpen(false);
      fetchUsers();
    } catch (error) {
      toast({
        title: '❌ Error',
        description: error.response?.data?.detail || 'Failed to change plan',
        variant: 'destructive',
      });
    } finally {
      setChangingPlan(false);
    }
  };

  // Filter users by search term
  const filteredUsers = users.filter(user => {
    const search = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.phone?.includes(search) ||
      user.clientId?.toString().includes(search) ||
      user.principalId?.toLowerCase().includes(search)
    );
  });

  const getPlanIcon = (plan) => {
    switch (plan) {
      case 'Basic': return <Package className="w-4 h-4" />;
      case 'Advanced': return <Rocket className="w-4 h-4" />;
      case 'Professional': return <Crown className="w-4 h-4" />;
      default: return null;
    }
  };

  const getPlanColor = (plan) => {
    switch (plan) {
      case 'Basic': return 'from-blue-400 to-blue-700';
      case 'Advanced': return 'from-blue-500 to-blue-500';
      case 'Professional': return 'from-orange-500 to-red-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-600">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Users Management</h2>
          <div className="text-sm text-slate-600">Total Users: {users.length}</div>
        </div>
        <div className="grid grid-cols-2 sm:flex sm:items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggleAssignmentMode}
            className="border-blue-300 hover:bg-blue-50"
          >
            Assignment: {assignmentMode === 'manual' ? '✋ Manual' : '🤖 Auto'}
          </Button>
          <Button size="sm" onClick={() => setAddOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="admin-add-client-btn">
            <UserPlus className="w-4 h-4 mr-1.5" /> Add Client
          </Button>
        </div>
      </div>
      <AddClientModal open={addOpen} onClose={() => setAddOpen(false)} onCreated={() => fetchUsers()} />
      <GenerateLoginModal user={loginUser} onClose={() => setLoginUser(null)} />

      {/* Search Bar */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search by Name, Email, Phone, Client ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4">
        {filteredUsers.length === 0 ? (
          <Card className="bg-white shadow-lg border-slate-200">
            <CardContent className="py-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-600">No users found</p>
            </CardContent>
          </Card>
        ) : (
          filteredUsers.map((user) => (
            <Card key={user.principalId} className="bg-white shadow-lg border-slate-200 transition-all" data-testid={`admin-user-card-${user.clientId}`}>
              <CardContent className="p-4 sm:pt-6 sm:px-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="space-y-2 flex-1 min-w-0">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-base sm:text-lg text-slate-800 break-words">{user.name}</h3>
                        {user.isActivated || user.activePlan ? (
                          <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[11px] font-semibold">Activated</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold">₹9 Pending</span>
                        )}
                        {user.createdBy === 'admin' && <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[11px] font-semibold">Added by Admin</span>}
                      </div>
                      <p className="text-sm text-slate-600 break-all">{user.email}</p>
                      <p className="text-sm text-slate-500">{user.phone}</p>
                    </div>
                    <div className="no-mobile-pad flex flex-wrap gap-x-4 gap-y-2 text-xs sm:text-sm">
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-500 font-medium">Client ID:</span>
                        <span className="font-mono text-blue-400">#{user.clientId}</span>
                        <button 
                          onClick={() => copyToClipboard(user.clientId?.toString(), `client-${user.principalId}`)}
                          className="hover:text-blue-400 transition-colors"
                        >
                          {copiedId === `client-${user.principalId}` ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3 text-slate-500" />}
                        </button>
                      </div>
                      <div className="flex items-center space-x-2 min-w-0">
                        <span className="text-slate-500 font-medium">Principal:</span>
                        <span className="font-mono text-xs text-blue-600 truncate">{user.principalId}</span>
                        <button 
                          onClick={() => copyToClipboard(user.principalId, `principal-${user.principalId}`)}
                          className="hover:text-blue-600 transition-colors"
                        >
                          {copiedId === `principal-${user.principalId}` ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3 text-slate-500" />}
                        </button>
                      </div>
                      <div>
                        <span className="text-slate-500 font-medium">Role:</span>
                        <span className="ml-2 px-2 py-1 rounded bg-blue-500/20 text-blue-300 text-xs font-medium">{user.role}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-500 font-medium">Plan:</span>
                        {user.activePlan ? (
                          <span className={`ml-2 px-2 py-1 rounded bg-gradient-to-r ${getPlanColor(user.activePlan)} text-slate-800 text-xs font-medium flex items-center space-x-1`}>
                            {getPlanIcon(user.activePlan)}
                            <span>{user.activePlan}</span>
                          </span>
                        ) : (
                          <span className="ml-2 px-2 py-1 rounded bg-slate-500/20 text-slate-500 text-xs font-medium">No Plan</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-500 font-medium">Agent:</span>
                        {user.assignedAgent ? (
                          <span className="ml-2 px-2 py-1 rounded bg-green-500/20 text-green-600 text-xs font-medium">
                            {agents.find(a => a.agentId === user.assignedAgent)?.name || 'Assigned'}
                          </span>
                        ) : (
                          <span className="ml-2 px-2 py-1 rounded bg-slate-500/20 text-slate-500 text-xs font-medium">Unassigned</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="no-mobile-pad grid grid-cols-6 md:flex md:flex-wrap items-center gap-1.5 md:gap-2 border-t md:border-t-0 border-slate-100 pt-3 md:pt-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLoginUser(user)}
                      className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                      title="Generate Login Password"
                      data-testid={`admin-generate-login-${user.clientId}`}
                    >
                      <KeyRound className="w-4 h-4 md:mr-1" /><span className="hidden md:inline">Login</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActivation(user)}
                      className={user.isActivated ? 'hover:bg-amber-50 text-amber-600' : 'hover:bg-green-50 text-green-600'}
                      title={user.isActivated ? 'Lock dashboard' : 'Unlock dashboard (skip ₹9)'}
                      data-testid={`admin-toggle-activation-${user.clientId}`}
                    >
                      {user.isActivated ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenAssignAgent(user)}
                      className="hover:bg-green-100 text-green-600"
                      title="Assign Agent"
                    >
                      <Users className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenChangePlan(user)}
                      className="hover:bg-blue-100 text-blue-600 hover:text-purple-300"
                      title="Change Plan"
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewOverview(user)}
                      className="hover:bg-blue-500/20 text-blue-400 hover:text-blue-300"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteUser(user.principalId)}
                      className="bg-red-500/20 hover:bg-red-500/30 text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Change Plan Dialog */}
      <Dialog open={changePlanOpen} onOpenChange={setChangePlanOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent flex items-center">
              <Settings className="w-5 h-5 mr-2 text-blue-600" />
              Change User Plan
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <p className="text-slate-500 text-sm mb-1">User</p>
              <p className="font-semibold text-slate-800">{selectedUser?.name}</p>
              <p className="text-xs text-slate-500">#{selectedUser?.clientId} • {selectedUser?.email}</p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-slate-600 font-medium">Select Plan</label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger className="bg-white border-slate-200 text-slate-800">
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="none" className="text-slate-600">
                    <span className="flex items-center space-x-2">
                      <X className="w-4 h-4" />
                      <span>No Plan (Remove)</span>
                    </span>
                  </SelectItem>
                  <SelectItem value="Basic" className="text-cyan-400">
                    <span className="flex items-center space-x-2">
                      <Package className="w-4 h-4" />
                      <span>Basic - ₹999</span>
                    </span>
                  </SelectItem>
                  <SelectItem value="Advanced" className="text-blue-600">
                    <span className="flex items-center space-x-2">
                      <Rocket className="w-4 h-4" />
                      <span>Advanced - ₹1999</span>
                    </span>
                  </SelectItem>
                  <SelectItem value="Professional" className="text-orange-400">
                    <span className="flex items-center space-x-2">
                      <Crown className="w-4 h-4" />
                      <span>Professional - ₹2999</span>
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex space-x-3 pt-4">
              <Button
                variant="ghost"
                onClick={() => setChangePlanOpen(false)}
                className="flex-1 border border-slate-200"
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangePlan}
                disabled={changingPlan}
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-pink-600"
              >
                {changingPlan ? (
                  <span className="flex items-center">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Changing...
                  </span>
                ) : (
                  'Change Plan'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Overview Dialog */}
      <Dialog open={overviewOpen} onOpenChange={setOverviewOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-blue-600">
              User Overview - #{selectedUser?.clientId}
            </DialogTitle>
          </DialogHeader>
          
          {loadingOverview ? (
            <div className="py-12 text-center">
              <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-600">Loading user data...</p>
            </div>
          ) : userOverview ? (
            <div className="space-y-6">
              {/* User Info */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg text-blue-400">User Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Name</p>
                      <p className="text-slate-800 font-medium">{userOverview.user?.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Email</p>
                      <p className="text-slate-800">{userOverview.user?.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Phone</p>
                      <p className="text-slate-800">{userOverview.user?.phone}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Client ID</p>
                      <p className="text-blue-400 font-bold">#{userOverview.user?.clientId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Role</p>
                      <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-300 text-xs font-medium">{userOverview.user?.role}</span>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Active Plan</p>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${userOverview.user?.activePlan ? 'bg-green-100 text-green-300' : 'bg-slate-500/20 text-slate-500'}`}>
                        {userOverview.user?.activePlan || 'No Plan'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/10 border-blue-500/30">
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-600 font-medium">Total Orders</p>
                    <p className="text-2xl md:text-3xl font-bold text-blue-400 mt-2">{userOverview.stats?.totalOrders || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-200">
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-600 font-medium">Delivered</p>
                    <p className="text-2xl md:text-3xl font-bold text-green-600 mt-2">{userOverview.stats?.deliveredOrders || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-500/10 to-rose-500/10 border-red-500/30">
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-600 font-medium">RTO</p>
                    <p className="text-2xl md:text-3xl font-bold text-red-400 mt-2">{userOverview.stats?.rtoOrders || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/10 border-slate-200">
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-600 font-medium">Total Payments</p>
                    <p className="text-2xl md:text-3xl font-bold text-blue-600 mt-2">₹{userOverview.stats?.totalPayments?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Onboarding Status */}
              {userOverview.onboarding && (
                <Card className="bg-white border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg text-cyan-400">Onboarding Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {['purchaseStatus', 'whatsappStatus', 'websiteStatus', 'campaignStatus', 'adsStatus'].map((step) => (
                        <div key={step} className="text-center">
                          <p className="text-xs text-slate-500 font-medium mb-2 capitalize">{step.replace('Status', '')}</p>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            userOverview.onboarding[step] === 'Completed' ? 'bg-green-100 text-green-300' :
                            userOverview.onboarding[step] === 'Live' ? 'bg-blue-500/20 text-blue-300' :
                            userOverview.onboarding[step] === 'Pending' ? 'bg-yellow-500/20 text-yellow-300' :
                            'bg-slate-500/20 text-slate-500'
                          }`}>
                            {userOverview.onboarding[step] || 'Not Started'}
                          </span>
                        </div>
                      ))}
                    </div>
                    {userOverview.onboarding.websiteLink && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <p className="text-xs text-slate-500 font-medium mb-1">Website Link</p>
                        <a href={userOverview.onboarding.websiteLink} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 hover:underline">
                          {userOverview.onboarding.websiteLink}
                        </a>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Recent Orders */}
              {userOverview.recentOrders?.length > 0 && (
                <Card className="bg-white border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg text-orange-400">Recent Orders</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {userOverview.recentOrders.map((order, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <div>
                            <p className="text-slate-800 font-medium">{order.orderId}</p>
                            <p className="text-sm text-slate-500">{order.productName}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-green-600 font-bold">₹{order.price}</p>
                            <span className={`text-xs px-2 py-1 rounded ${
                              order.status === 'Delivered' ? 'bg-green-100 text-green-300' :
                              order.status === 'RTO' ? 'bg-red-500/20 text-red-300' :
                              'bg-blue-500/20 text-blue-300'
                            }`}>{order.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Payments */}
              {userOverview.recentPayments?.length > 0 && (
                <Card className="bg-white border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg text-green-600">Recent Payments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {userOverview.recentPayments.map((payment, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <div>
                            <p className="text-slate-800 font-medium">{payment.paymentId}</p>
                            <p className="text-sm text-slate-500">{payment.planName || 'Payment'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-green-600 font-bold">₹{payment.planPrice || payment.amount || 0}</p>
                            <span className={`text-xs px-2 py-1 rounded ${
                              payment.status === 'Successful' ? 'bg-green-100 text-green-300' :
                              payment.status === 'Pending' ? 'bg-yellow-500/20 text-yellow-300' :
                              'bg-red-500/20 text-red-300'
                            }`}>{payment.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="py-12 text-center">
              <p className="text-slate-500">No data available</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Agent Dialog */}
      <Dialog open={assignAgentOpen} onOpenChange={setAssignAgentOpen}>
        <DialogContent className="bg-white text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-800">Assign Sales Agent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm text-slate-600 mb-2">Client: <span className="font-semibold">{selectedUser?.name}</span></p>
              <p className="text-xs text-slate-500">Current Agent: {selectedUser?.assignedAgent ? agents.find(a => a.agentId === selectedUser.assignedAgent)?.name || 'Assigned' : 'None'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Select Agent</label>
              <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                <SelectTrigger className="w-full bg-white border-slate-300 text-slate-800">
                  <SelectValue placeholder="Choose an agent..." />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none" className="text-slate-800">Unassign</SelectItem>
                  <SelectItem value="auto" className="text-blue-600 font-medium">🤖 Auto-Assign (Round Robin)</SelectItem>
                  {agents.filter(a => a.isActive).map((agent) => (
                    <SelectItem key={agent.agentId} value={agent.agentId} className="text-slate-800">
                      {agent.name} ({agent.totalClients || 0} clients)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="outline" onClick={() => setAssignAgentOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAssignAgent} 
                disabled={assigning}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {assigning ? 'Assigning...' : 'Assign Agent'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManagement;
